import {
  Box,
  Button,
  Flex,
  H2,
  H4,
  Icon,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_TableOptions,
  OverflowTooltip,
  RowActionButton,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { BASE_ADMIN_DASHBOARD_API } from '../../utils/constants';
import { fetchWithAuthV2 } from '../../utils/requests';

type SavedRecording = {
  saved_drive_id: number;
  saved_at: string;
  recording_title: string;
  source_drive_id: number;
  source_org_id: number;
  source_org_name: string;
  target_org_id: number;
  target_org_name: string;
  saved_by_email?: string | null;
};

type SaveAuditResponse = {
  saves: SavedRecording[];
  total: number;
};

const SAVE_AUDIT_QUERY_KEY = ['save-audit'] as const;

// Stable empty-array reference so `saves` doesn't get a new identity every
// render while the query has no data yet (which would defeat sortedSaves'
// useMemo below).
const EMPTY_SAVES: SavedRecording[] = [];

const fetchSaveAuditRecordings = async (): Promise<SaveAuditResponse> =>
  (await fetchWithAuthV2(`${BASE_ADMIN_DASHBOARD_API}/save_audit`, {
    method: 'GET',
    fallbackErrorMessage: 'Failed to load saved recordings audit',
  })) as SaveAuditResponse;

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<SavedRecording>>(
    MaterialTableWrapper
  );

const formatSavedAt = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const renderOverflowCell = (value: string, dataTestId: string) => (
  <OverflowTooltip
    value={value}
    dataTestId={dataTestId}
    style={{ width: '100%' }}
  />
);

export default function SavedRecordings() {
  const { showToast } = useChakraHook();
  const queryClient = useQueryClient();

  const {
    data: saveAuditResponse,
    isFetching: isLoading,
    error,
  } = useQuery({
    queryKey: SAVE_AUDIT_QUERY_KEY,
    queryFn: fetchSaveAuditRecordings,
  });

  // On error, show empty rather than stale data from a previous successful
  // load (react-query otherwise keeps the last-known-good `data` around).
  const saves = error ? EMPTY_SAVES : saveAuditResponse?.saves ?? EMPTY_SAVES;
  const total = error ? 0 : saveAuditResponse?.total ?? 0;

  useEffect(() => {
    if (error) {
      showToast({
        title: 'Failed to load saved recordings',
        description: `${error}`,
        status: 'error',
      });
    }
  }, [error, showToast]);

  const onRefreshSavedRecordings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SAVE_AUDIT_QUERY_KEY });
  }, [queryClient]);

  const sortedSaves = useMemo(
    () =>
      [...saves].sort(
        (left, right) =>
          new Date(right.saved_at).getTime() - new Date(left.saved_at).getTime()
      ),
    [saves]
  );

  const columns = useMemo<MRT_ColumnDef<SavedRecording>[]>(() => {
    const baseColumns: MRT_ColumnDef<SavedRecording>[] = [
      {
        accessorKey: 'saved_at',
        header: 'Saved At',
        size: 180,
        minSize: 160,
        grow: false,
        Cell: ({ row }) => formatSavedAt(row.original.saved_at),
      },
      {
        accessorKey: 'recording_title',
        header: 'Recording',
        minSize: 240,
        size: 280,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'saved-recordings-recording-title'
          ),
      },
      {
        accessorKey: 'source_org_name',
        header: 'Source Organization',
        minSize: 180,
        size: 200,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'saved-recordings-source-organization'
          ),
      },
      {
        accessorKey: 'target_org_name',
        header: 'Target Organization',
        minSize: 180,
        size: 200,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'saved-recordings-target-organization'
          ),
      },
      {
        accessorKey: 'saved_by_email',
        header: 'Save Initiator',
        minSize: 180,
        size: 220,
        grow: false,
        Cell: ({ row }) =>
          renderOverflowCell(
            row.original.saved_by_email ?? '',
            'saved-recordings-initiator'
          ),
      },
    ];

    return baseColumns;
  }, []);

  const openPrivateVizPage = useCallback(
    (save: SavedRecording) => {
      if (isLoading || !save.source_drive_id) return;
      window.open(
        `/organization/1/drive/${save.source_drive_id}`,
        '_blank',
        'noopener,noreferrer'
      );
    },
    [isLoading]
  );

  const tableOptions: MRT_TableOptions<SavedRecording> = useMemo(
    () => ({
      columns,
      data: sortedSaves,
      initialState: {
        pagination: { pageSize: 50, pageIndex: 0 },
        columnPinning: {
          left: ['saved_at'],
          right: ['mrt-row-actions'],
        },
        showGlobalFilter: true,
      },
      enableRowActions: true,
      enableSorting: true,
      enableToolbarInternalActions: false,
      positionActionsColumn: 'last',
      positionGlobalFilter: 'right',
      muiTablePaperProps: {
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
      muiTableContainerProps: {
        sx: {
          border: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.12)',
          maxHeight: 'none',
          overflowY: 'visible',
        },
      },
      muiTableHeadCellProps: ({ column }) => ({
        sx: {},
        'data-testid': `header-${column.id}`,
      }),
      muiTableBodyCellProps: ({ column }) => {
        const constrainedColumns = new Set([
          'saved_by_email',
          'recording_title',
          'source_org_name',
          'target_org_name',
        ]);

        if (column.id === 'mrt-row-actions') {
          return {
            'data-testid': `row-${column.id}`,
            style: {
              whiteSpace: 'nowrap',
            },
          };
        }

        if (constrainedColumns.has(column.id)) {
          const maxWidthByColumn: Record<string, string> = {
            saved_by_email: '220px',
            recording_title: '280px',
            source_org_name: '200px',
            target_org_name: '200px',
          };

          return {
            'data-testid': `row-${column.id}`,
            style: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: maxWidthByColumn[column.id],
            },
          };
        }

        return {
          'data-testid': `row-${column.id}`,
        };
      },
      muiPaginationProps: {
        rowsPerPageOptions: [25, 50, 100, 200],
      },
      getRowId: (originalRow) =>
        `${originalRow.saved_drive_id}-${originalRow.target_org_id}-${originalRow.saved_at}`,
      renderRowActions: ({ row }) => (
        <Box flexWrap="nowrap" display="flex" alignItems="center">
          <RowActionButton<SavedRecording>
            title="Open source recording private link"
            icon={<Icon icon="openInNew" />}
            row={row.original}
            onClickHandler={openPrivateVizPage}
          />
        </Box>
      ),
      state: {
        isLoading,
        showLoadingOverlay: false,
        showAlertBanner: false,
        showSkeletons: isLoading,
      },
    }),
    [columns, isLoading, openPrivateVizPage, sortedSaves]
  );

  return (
    <Box
      h="full"
      p="25px 36px 0px"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Flex gap={4} alignItems="center" justifyContent="space-between">
        <H2 fontWeight="medium" fontSize={30}>
          Saved Recordings
        </H2>
        <Button
          onClick={onRefreshSavedRecordings}
          variant="outline"
          minWidth="min-content"
          data-testid="refresh-saved-recordings-button"
          leftIcon="refresh"
          disabled={isLoading}
        >
          Refresh List
        </Button>
      </Flex>
      <Box position="relative" h="full">
        <H4
          color="gray.300"
          fontSize={14}
          position="absolute"
          left={0}
          top={4}
          zIndex={2}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Box
            borderRadius="full"
            w={2}
            h={2}
            bg="green.300"
            display="inline-block"
            aria-hidden="true"
          />
          <span style={{ lineHeight: 1 }}>
            Total saved recording entries: {total}
          </span>
        </H4>
        <Box zIndex={1}>
          <ThemedMaterialTableWrapper {...tableOptions} />
        </Box>
      </Box>
    </Box>
  );
}
